import React, { useState, useMemo, useEffect } from "react";
import { X, Lock, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../../ui-v2";
import { cn } from "../../../../lib/cn";

/**
 * BloquearBulkModal — modal para bloquear MÚLTIPLES rangos horarios
 * de golpe.
 *
 * Se usa para dos flujos nuevos:
 *   - "Bloquear todo el día": rangos = todas las franjas del día.
 *   - "Bloquear N slots seleccionados": rangos = slots que el usuario
 *     marcó individualmente.
 *
 * La diferencia con BloquearFranjaModal (1 rango, horas editables) es
 * que aquí los rangos son FIJOS (vienen de arriba) y solo se muestran
 * como lista para confirmación.
 *
 * Props:
 *   open, onClose, onSave
 *   titulo: string — "Bloquear día completo" | "Bloquear slots seleccionados"
 *   fecha: "YYYY-MM-DD"
 *   rangos: [{hora_inicio, hora_fin}, ...]
 *   esAdmin, tecnicos, tecnicoIdActual, fechaLimiteCampana
 *
 * Al guardar, dispara onSave con array de bloqueos calculados:
 *   fechas (según recurrencia) × rangos = array de {persona_id, fecha, hora_inicio, hora_fin, motivo?}
 */

const DIAS_LABEL = ["", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function fechaADiaISO(yyyymmdd) {
  if (!yyyymmdd) return null;
  const d = new Date(`${yyyymmdd}T12:00:00`);
  const js = d.getDay();
  return js === 0 ? 7 : js;
}

function fechasRecurrentes(desde, hasta, diaISO) {
  if (!desde || !hasta || !diaISO) return [];
  const inicio = new Date(`${desde}T12:00:00`);
  const fin = new Date(`${hasta}T12:00:00`);
  if (inicio > fin) return [];
  const fechas = [];
  const cur = new Date(inicio);
  while (cur <= fin) {
    const js = cur.getDay();
    const iso = js === 0 ? 7 : js;
    if (iso === diaISO) {
      const yyyy = cur.getFullYear();
      const mm = String(cur.getMonth() + 1).padStart(2, "0");
      const dd = String(cur.getDate()).padStart(2, "0");
      fechas.push(`${yyyy}-${mm}-${dd}`);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return fechas;
}

function normalizarHora(v) {
  if (!v) return "";
  return String(v).length === 5 ? `${v}:00` : String(v);
}

// ────────────────────────────────────────────────────────────────

export default function BloquearBulkModal({
  open, onClose, onSave,
  titulo = "Bloquear",
  fecha,
  rangos = [],
  esAdmin = false,
  tecnicos = null,
  tecnicoIdActual = null,
  fechaLimiteCampana,
}) {

  const [tecnicoId, setTecnicoId] = useState(tecnicoIdActual);
  const [recurrencia, setRecurrencia] = useState("solo_dia");
  const [rangoDesde, setRangoDesde] = useState(fecha);
  const [rangoHasta, setRangoHasta] = useState(fechaLimiteCampana || fecha);
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTecnicoId(tecnicoIdActual);
    setRecurrencia("solo_dia");
    setRangoDesde(fecha);
    setRangoHasta(fechaLimiteCampana || fecha);
    setMotivo("");
    setError(null);
  }, [open, fecha, tecnicoIdActual, fechaLimiteCampana]);

  const diaISO = useMemo(() => fechaADiaISO(fecha), [fecha]);
  const diaLabel = DIAS_LABEL[diaISO] || "";

  const fechasObjetivo = useMemo(() => {
    if (recurrencia === "solo_dia") return [fecha];
    if (recurrencia === "todos_campana") {
      return fechasRecurrentes(fecha, fechaLimiteCampana, diaISO);
    }
    if (recurrencia === "rango") {
      return fechasRecurrentes(rangoDesde, rangoHasta, diaISO);
    }
    return [];
  }, [recurrencia, fecha, fechaLimiteCampana, diaISO, rangoDesde, rangoHasta]);

  const totalBloqueos = fechasObjetivo.length * rangos.length;

  const puedeGuardar =
    !!tecnicoId &&
    rangos.length > 0 &&
    fechasObjetivo.length > 0 &&
    !guardando;

  async function guardar() {
    if (!puedeGuardar) return;
    setGuardando(true);
    setError(null);
    try {
      // Producto cartesiano: fechas × rangos
      const bloqueos = [];
      for (const f of fechasObjetivo) {
        for (const r of rangos) {
          bloqueos.push({
            persona_id: tecnicoId,
            fecha: f,
            hora_inicio: normalizarHora(r.hora_inicio),
            hora_fin: normalizarHora(r.hora_fin),
            motivo: motivo.trim() || null,
          });
        }
      }
      await onSave(bloqueos);
      onClose();
    } catch (e) {
      console.error("[BloquearBulkModal] guardar", e);
      setError(e?.response?.data?.error || "No se pudo guardar el bloqueo.");
    } finally {
      setGuardando(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-900">{titulo}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="px-5 py-4 space-y-4">

          {/* Contexto: día + rangos */}
          <div className="bg-slate-50 rounded-md p-3">
            <div className="text-sm text-slate-700">
              <strong>{diaLabel}</strong>{" "}
              <span className="text-slate-500">
                {fecha && new Date(`${fecha}T12:00:00`).toLocaleDateString("es-ES", {
                  day: "numeric", month: "long",
                })}
              </span>
            </div>
            <div className="mt-2 space-y-0.5">
              {rangos.map((r, i) => (
                <div key={i} className="text-xs text-slate-600 tabular-nums">
                  · {String(r.hora_inicio).slice(0, 5)} → {String(r.hora_fin).slice(0, 5)}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5">
              {rangos.length} {rangos.length === 1 ? "rango" : "rangos"} · duración total{" "}
              {rangos.reduce((acc, r) => {
                const ini = parseInt(String(r.hora_inicio).slice(0, 2), 10) * 60 +
                            parseInt(String(r.hora_inicio).slice(3, 5), 10);
                const fin = parseInt(String(r.hora_fin).slice(0, 2), 10) * 60 +
                            parseInt(String(r.hora_fin).slice(3, 5), 10);
                return acc + (fin - ini);
              }, 0)} min
            </div>
          </div>

          {/* Selector técnico */}
          {esAdmin && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Técnico a bloquear *
              </label>
              <select
                value={tecnicoId || ""}
                onChange={(e) => setTecnicoId(parseInt(e.target.value, 10) || null)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="">— Selecciona técnico —</option>
                {(tecnicos || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Recurrencia */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Aplicar a
            </label>
            <div className="space-y-2">
              <OpcionRecurrencia
                checked={recurrencia === "solo_dia"}
                onClick={() => setRecurrencia("solo_dia")}
                label="Solo este día"
                hint={fecha && new Date(`${fecha}T12:00:00`).toLocaleDateString("es-ES")}
              />
              <OpcionRecurrencia
                checked={recurrencia === "todos_campana"}
                onClick={() => setRecurrencia("todos_campana")}
                label={`Todos los ${diaLabel.toLowerCase()} de la campaña`}
                hint={
                  fechaLimiteCampana
                    ? `Hasta ${new Date(`${fechaLimiteCampana}T12:00:00`).toLocaleDateString("es-ES")}`
                    : "Sin fecha límite definida"
                }
                disabled={!fechaLimiteCampana}
              />
              <OpcionRecurrencia
                checked={recurrencia === "rango"}
                onClick={() => setRecurrencia("rango")}
                label={`Todos los ${diaLabel.toLowerCase()} en un rango personalizado`}
              />

              {recurrencia === "rango" && (
                <div className="grid grid-cols-2 gap-2 ml-7">
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">Desde</label>
                    <input
                      type="date"
                      value={rangoDesde}
                      onChange={(e) => setRangoDesde(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">Hasta</label>
                    <input
                      type="date"
                      value={rangoHasta}
                      onChange={(e) => setRangoHasta(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {totalBloqueos > 0 && (
              <p className="text-[11px] text-slate-500 mt-2">
                Se crearán{" "}
                <strong className="text-slate-700">{totalBloqueos}</strong>{" "}
                {totalBloqueos === 1 ? "bloqueo" : "bloqueos"}{" "}
                ({fechasObjetivo.length} {fechasObjetivo.length === 1 ? "día" : "días"} ×{" "}
                {rangos.length} {rangos.length === 1 ? "rango" : "rangos"}).
              </p>
            )}
          </div>

          {/* Motivo */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Motivo (opcional)
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              maxLength={255}
              placeholder="Se aplicará a todos los bloqueos generados."
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
            />
          </div>

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-900 px-3 py-1.5"
            disabled={guardando}
          >
            Cancelar
          </button>
          <Button onClick={guardar} disabled={!puedeGuardar}>
            {guardando ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Lock className="w-3.5 h-3.5" />
                Bloquear{totalBloqueos > 1 && ` (${totalBloqueos})`}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function OpcionRecurrencia({ checked, onClick, label, hint, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-md border transition-colors",
        checked && !disabled && "border-primary bg-primary/5",
        !checked && !disabled && "border-slate-200 hover:border-slate-300",
        disabled && "border-slate-100 bg-slate-50 cursor-not-allowed opacity-60"
      )}
    >
      <span className={cn(
        "shrink-0 w-3.5 h-3.5 rounded-full border-2 mt-0.5",
        checked && !disabled ? "border-primary bg-primary" : "border-slate-300"
      )}>
        {checked && !disabled && (
          <span className="block w-1 h-1 rounded-full bg-white mx-auto mt-[3px]" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className={cn(
          "text-sm",
          checked && !disabled ? "text-slate-900 font-medium" : "text-slate-700"
        )}>
          {label}
        </div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </button>
  );
}
